import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Avatar,
  Chip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Leaderboard as LeaderboardIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import tournamentService from '../services/tournamentService';

// Mock Leaderboard Data
const mockLeaderboard = [
  { rank: 1, id: 101, username: "AnaLegend", avatarUrl: "https://mui.com/static/images/avatar/1.jpg", wins: 5, losses: 1, score: 15 },
  { rank: 2, id: 104, username: "DavidCardMaster", avatarUrl: "https://mui.com/static/images/avatar/4.jpg", wins: 4, losses: 2, score: 12 },
  { rank: 3, id: 102, username: "CarlosGamer", avatarUrl: "https://mui.com/static/images/avatar/2.jpg", wins: 4, losses: 2, score: 12 },
  { rank: 4, id: 105, username: "SofíaChampion", avatarUrl: "https://mui.com/static/images/avatar/5.jpg", wins: 3, losses: 3, score: 9 },
  { rank: 5, id: 103, username: "ElenaProPlayer", avatarUrl: "https://mui.com/static/images/avatar/3.jpg", wins: 2, losses: 4, score: 6 },
  { rank: 6, id: 106, username: "PedroRookie", avatarUrl: "https://mui.com/static/images/avatar/6.jpg", wins: 1, losses: 5, score: 3 },
];

const TournamentLeaderboardPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      try {
        setLoading(true);
        // Fetch basic tournament info (or use mock)
        const mockTournamentData = {
          id: id,
          name: `Torneo de Prueba ${id}`,
        };
        setTournament(mockTournamentData);
        // const data = await tournamentService.getTournamentById(id);
        // setTournament(data);

        // Set mock leaderboard data
        setLeaderboard(mockLeaderboard);

      } catch (error) {
        console.error('Error fetching tournament details:', error);
        setError('Error al cargar los detalles del torneo');
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [id]);

  const handleBack = () => {
    navigate(`/tournaments/${id}`);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tournament) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', gap: 2 }}>
        <Alert severity="error">{error || 'No se encontraron datos del torneo'}</Alert>
        <Button variant="contained" onClick={() => navigate(`/tournaments/${id}`)} startIcon={<ArrowBackIcon />}>
          Volver al Torneo
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 1 }, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '90%', maxWidth: '1000px', mx: 'auto', bgcolor: 'transparent' }}>
      <Stack spacing={2} sx={{ width: '100%', bgcolor: 'transparent' }}>
        {/* Header */}
        <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <IconButton onClick={handleBack} sx={{ width: 36, height: 36, bgcolor: 'action.hover', color: 'text.primary', '&:hover': { bgcolor: 'primary.main', color: 'white', transform: 'scale(1.1)' }, transition: 'all 0.2s ease-in-out' }}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Box>
                <Typography variant="h6" fontWeight="bold">Tabla de Posiciones</Typography>
                <Typography variant="body2" color="text.secondary">{tournament.name}</Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Leaderboard Table */}
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table aria-label="tabla de posiciones">
            <TableHead sx={{ bgcolor: 'rgba(200, 158, 56, 0.05)' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', color: '#c89e38', width: '60px' }}>#</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#c89e38' }}>Jugador</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#c89e38' }}>Victorias</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', color: '#c89e38' }}>Derrotas</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: '#c89e38' }}>Puntaje</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leaderboard.map((player) => (
                <TableRow
                  key={player.id}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    bgcolor: player.rank <= 3 ? 'action.hover' : 'transparent',
                    '&:hover': { bgcolor: 'rgba(200, 158, 56, 0.08)' }
                  }}
                >
                  <TableCell component="th" scope="row">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography fontWeight="bold" color={player.rank <= 3 ? '#c89e38' : 'text.primary'}>
                        {player.rank}
                      </Typography>
                      {player.rank === 1 && <TrophyIcon sx={{ color: '#FFD700', fontSize: 18 }} />}
                      {player.rank === 2 && <TrophyIcon sx={{ color: '#C0C0C0', fontSize: 18 }} />}
                      {player.rank === 3 && <TrophyIcon sx={{ color: '#CD7F32', fontSize: 18 }} />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar src={player.avatarUrl} alt={player.username} sx={{ width: 32, height: 32 }} />
                      <Typography variant="body2" fontWeight={player.rank <= 3 ? 500 : 'normal'}>
                        {player.username}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" color="success.main">{player.wins}</Typography>
                  </TableCell>
                  <TableCell align="center">
                     <Typography variant="body2" color="error.main">{player.losses}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip 
                      label={player.score}
                      size="small"
                      sx={{ 
                        fontWeight: 'bold', 
                        bgcolor: '#c89e38', 
                        color: 'white'
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Back Button */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Button variant="outlined" onClick={handleBack} startIcon={<ArrowBackIcon />} sx={{ borderRadius: 1.5, textTransform: 'none', borderColor: '#c89e38', color: '#c89e38', '&:hover': { borderColor: '#b38d2d', bgcolor: 'rgba(200, 158, 56, 0.04)' } }}>
            Volver al Torneo
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default TournamentLeaderboardPage; 